import { ReactNode } from "react";
import styled, { css, useTheme } from "styled-components";
import { LARGE_MOBILE } from "common/constants";
import cornixHorizontalLogo from "images/svg_logo_horizontal.svg";
import cornixWhiteHorizontalLogo from "images/svg_white_logo_horizontal.svg";
import LanguageSelectorPopover from "common/Home/Header/LanguageSelectorPopover.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";
import AuthHero from "features/auth/newLook/AuthHero";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens";

// Sign-in pages in the new look. The form sits straight on the page (no card
// around it), so it reads as the content. Desktop: the cornix.io pitch to the
// left of the form. Tablet and phone: the form alone under a small brand
// header. The security checklist closes the form as quiet meta under a
// hairline. White-label brands never show the Cornix hero.
//
// Phones get touch sizing (44px fields and buttons), a slightly larger title,
// 20px gutters and safe-area padding (notch, home indicator, landscape).

export const AUTH_HERO_MIN_WIDTH = 1100;
const HERO = `@media (min-width: ${AUTH_HERO_MIN_WIDTH}px)`;
const PHONE = `@media (max-width: ${LARGE_MOBILE})`;
const PHONE_GUTTER = 20;
// Sides add the gutter to the inset (landscape notch); top and bottom only
// need to clear it.
const safeArea = (side: "top" | "right" | "bottom" | "left", min: number) =>
  side === "top" || side === "bottom"
    ? `max(${min}px, env(safe-area-inset-${side}, 0px))`
    : `calc(${min}px + env(safe-area-inset-${side}, 0px))`;

interface NewLookAuthLayoutProps {
  children: ReactNode;
  /** Shown under the form (the security checklist). */
  below?: ReactNode;
  showLanguageSelector?: boolean;
}

const NewLookAuthLayout = ({
  children,
  below,
  showLanguageSelector = true,
}: NewLookAuthLayoutProps) => {
  const theme = useTheme();
  const { brandingData } = useBranding();
  const isWhitelabel = !!brandingData?.whitelabel;
  const { t } = useTypedTranslation(LanguageNameSpaces.AUTH);

  const logoSrc = isWhitelabel
    ? theme.isDarkMode
      ? brandingData?.dark_mode_logo
      : brandingData?.light_mode_logo
    : theme.isDarkMode
    ? cornixWhiteHorizontalLogo
    : cornixHorizontalLogo;
  const logo = logoSrc ? <Logo src={logoSrc} alt="logo" /> : null;
  const copyright = `© 2018 - ${new Date().getFullYear()}${
    isWhitelabel ? "" : " Cornix.io"
  }. ${t("copyright.all_rights_reserved")}`;
  const hero = !isWhitelabel;

  return (
    <Root $hero={hero}>
      {hero && (
        <HeroColumn>
          <AuthHero logo={logo} footer={copyright} />
        </HeroColumn>
      )}
      <Main $hero={hero}>
        <Header $hero={hero}>
          <HeaderLogo $hero={hero}>{logo}</HeaderLogo>
          {showLanguageSelector && <LanguageSelectorPopover />}
        </Header>
        <Center>
          <FormBox>
            <FormBody>{children}</FormBody>
            {below && <Below>{below}</Below>}
          </FormBox>
        </Center>
        <Footer $hero={hero}>{copyright}</Footer>
      </Main>
    </Root>
  );
};

export default NewLookAuthLayout;

const Root = styled.div<{ $hero: boolean }>`
  box-sizing: border-box;
  width: 100%;
  height: 100vh;
  @supports (height: 100dvh) {
    height: 100dvh;
  }
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  background: ${(p) => p.theme.colors.canvasBgColor};
  color: ${(p) => p.theme.colors.primaryText};
  ${(p) =>
    p.$hero &&
    css`
      ${HERO} {
        /* On very wide screens the halves stop growing apart: the pitch
           hugs the centre line and the form sits close to it. */
        grid-template-columns: minmax(0, 1fr) minmax(480px, 1fr);
      }
    `}
`;
const HeroColumn = styled.div`
  display: none;
  ${HERO} {
    display: block;
    box-sizing: border-box;
    height: 100vh;
    @supports (height: 100dvh) {
      height: 100dvh;
    }
    overflow: hidden;
  }
`;
const Main = styled.main<{ $hero: boolean }>`
  box-sizing: border-box;
  min-width: 0;
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
`;
const Header = styled.header<{ $hero: boolean }>`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 40px;
  padding: 20px 24px;
  ${PHONE} {
    min-height: 44px;
    padding: ${safeArea("top", 10)} ${safeArea("right", PHONE_GUTTER - 6)} 0
      ${safeArea("left", PHONE_GUTTER)};
  }
  ${(p) =>
    p.$hero &&
    css`
      ${HERO} {
        justify-content: flex-end;
        padding: 24px 32px 0;
      }
    `}
`;
const HeaderLogo = styled.div<{ $hero: boolean }>`
  display: flex;
  align-items: center;
  min-width: 0;
  ${(p) =>
    p.$hero &&
    css`
      ${HERO} {
        display: none;
      }
    `}
`;
const Logo = styled.img`
  display: block;
  height: 28px;
  max-width: 180px;
  object-fit: contain;
  object-position: left center;
  ${PHONE} {
    height: 24px;
  }
`;
const Center = styled.div`
  flex: 1 0 auto;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 16px 24px 32px;
  ${PHONE} {
    justify-content: flex-start;
    padding: 20px ${safeArea("right", PHONE_GUTTER)} 32px
      ${safeArea("left", PHONE_GUTTER)};
  }
`;
// The form column. Its body holds the step; the security checklist sits
// under it, after a hairline.
const FormBox = styled.div`
  position: relative;
  box-sizing: border-box;
  width: 100%;
  max-width: 400px;
  /* No card around the form on the white page: the form is the content. */
  background: transparent;
`;
const FormBody = styled.div`
  padding: 0;
  /* Field labels in the label role (the shared Form colours them ink). */
  &&&& .ant-form-item-label {
    padding-bottom: 6px;
    > label {
      ${newLookType.label};
      color: ${(p) => p.theme.colors.secondaryText};
    }
  }
  /* Phones: thumb-sized fields and actions (44px, the touch minimum), labels
     a touch larger. Text inputs stay 16px so iOS does not zoom on focus. */
  ${PHONE} {
    &&&& .ant-form-item-label {
      padding-bottom: 8px;
      > label {
        font-size: 13px;
        line-height: 18px;
      }
    }
    &&&& .ant-input-lg,
    &&&& .ant-input-affix-wrapper-lg {
      padding-top: 9px;
      padding-bottom: 9px;
      font-size: 16px;
    }
    &&&& .ant-input-affix-wrapper-lg > input.ant-input-lg {
      padding-top: 0;
      padding-bottom: 0;
    }
    &&&& .ant-btn-primary {
      height: 44px;
    }
  }
`;
// The security checklist: quiet meta under the form, after a hairline.
const Below = styled.div`
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid ${(p) => p.theme.colors.cardBorderColor};
  ${PHONE} {
    margin-top: 32px;
  }
`;
const Footer = styled.footer<{ $hero: boolean }>`
  flex: none;
  padding: 0 20px 24px;
  ${PHONE} {
    padding: 0 ${safeArea("right", PHONE_GUTTER)} ${safeArea("bottom", 20)}
      ${safeArea("left", PHONE_GUTTER)};
  }
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  text-align: center;
  ${(p) =>
    p.$hero &&
    css`
      ${HERO} {
        display: none;
      }
    `}
`;
